import { trackError, trackEvent } from '@vp/om-newrelic-tracker';
import {
  NEWRELIC_BROWSER_AGENT_IDENTIFIER,
  ORDERS_UI_ERROR_TYPE,
  ORDERS_UI_EVENT_TYPE,
} from '../../constants/newRelic';
import type {
  TrackingErrorData,
  TrackingEventData,
} from '../../types/newRelic.types';
import { getQmReplayLink } from '../../utils/quaantumMetricUtils';

/** Removes config and headers from error object */
export const sanitizeError = (
  error: object & { config?: unknown; headers?: unknown }
): object => {
  const sanitized = { ...error };
  if (sanitized.config) delete sanitized.config;
  if (sanitized.headers) delete sanitized.headers;
  return sanitized;
};

export const trackErrorWrapper = (error: Error, errorData: object) => {
  const debug = process.env.NODE_ENV !== 'production';
  trackError(NEWRELIC_BROWSER_AGENT_IDENTIFIER, error, errorData, debug);
};

export const trackEventWrapper = (eventData: object) => {
  const debug = process.env['NODE_ENV'] !== 'production';
  trackEvent(
    NEWRELIC_BROWSER_AGENT_IDENTIFIER,
    ORDERS_UI_EVENT_TYPE,
    eventData,
    debug
  );
};

/**
 * Format event tracking data suitable for viewing in New Relic.
 * Spread Operator Usage: New Relic prefers flattened structure of data, nesting JSON will make querying difficult.
 * @param {string} eventReason queriable event type. Function will prepend ${ORDERS_UI_EVENT_TYPE} to the eventReason
 * @param {object} eventDataObj event data object
 */
export const createTrackingEventData = (
  eventReason: string,
  eventDataObj: object = {}
): TrackingEventData => {
  return {
    ...eventDataObj,
    eventName: ORDERS_UI_EVENT_TYPE,
    eventReason,
    qmReplay: getQmReplayLink(),
  };
};

/**
 * Format error tracking data suitable for viewing in New Relic.
 * @param {string} errorReason queryable error type. All errorReason values are prepended with 'orders-ui'.
 * @param {Error} errorObj error data object
 * @param {object} additionalData additional data to be included on the event
 * @returns {TrackingErrorData} - the formatted error data
 */
export const createTrackingErrorData = (
  errorReason: string,
  errorObj: Error,
  additionalData: object = {}
): TrackingErrorData => {
  return {
    ...additionalData,
    eventName: ORDERS_UI_ERROR_TYPE,
    errorName: errorReason, // optional field suggested by @vp/om-new-relic-tracker
    errorReason, // existing dashboards rely on errorReason
    error: JSON.stringify(sanitizeError(errorObj)),
    qmReplay: getQmReplayLink(),
  };
};
